<div class="text-fg">
  @if (loading()) {
    <div class="flex items-center justify-center gap-3 p-12 text-sm text-muted">
      <ui-spinner />
      <span>Loading hub…</span>
    </div>
  } @else if (error() && !hub()) {
    <div class="p-8 text-center text-danger-fg">
      <p class="mb-3">{{ error() }}</p>
      <button type="button" uiButton variant="outline" size="sm" (click)="load()">Retry</button>
    </div>
  } @else if (hub(); as h) {
    <header class="mb-5">
      <button type="button" class="mb-2 inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-sm text-muted hover:text-fg" (click)="goBack()">
        <ng-icon name="lucideArrowLeft" size="16" />
        Back
      </button>
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 class="m-0 flex items-center gap-2 text-[1.4rem] font-bold text-fg">
            <ng-icon name="lucideLayoutGrid" size="22" />
            {{ entityConfig.label }}
          </h1>
          <p class="mb-0 mt-1 text-sm text-muted">Operations hub — related webhooks, ledger, and shortcuts</p>
        </div>
        <div class="flex items-center gap-2">
          @if (canCheckStatus()) {
            <button type="button" uiButton variant="outline" size="sm" [disabled]="actionBusy()" (click)="checkStatus()">
              Check status
            </button>
          }
          <button type="button" uiButton variant="outline" size="sm" (click)="load()" title="Refresh">
            <ng-icon name="lucideRefreshCw" size="16" />
          </button>
        </div>
      </div>
    </header>

    @if (error()) {
      <div class="mb-4 flex items-center justify-between gap-2 rounded-lg border border-danger-border bg-danger-bg px-4 py-3 text-sm text-danger-fg" role="alert">
        <span>{{ error() }}</span>
        <button type="button" class="cursor-pointer border-0 bg-transparent px-1 text-danger-fg hover:opacity-70" aria-label="Dismiss" (click)="error.set(null)">&times;</button>
      </div>
    }

    <nav class="mb-4 flex gap-1 border-b border-border" aria-label="Operation sections">
      <button type="button" class="-mb-px cursor-pointer border-0 border-b-2 bg-transparent px-4 py-2 text-sm font-semibold" [class]="tab() === 'details' ? 'border-primary text-brand' : 'border-transparent text-muted hover:text-fg'" (click)="setTab('details')">Details</button>
      <button type="button" class="-mb-px cursor-pointer border-0 border-b-2 bg-transparent px-4 py-2 text-sm font-semibold" [class]="tab() === 'webhooks' ? 'border-primary text-brand' : 'border-transparent text-muted hover:text-fg'" (click)="setTab('webhooks')">
        Webhooks <span class="ml-1 rounded-full bg-surface-muted px-2 py-0.5 text-xs text-fg-secondary">{{ webhookCount() }}</span>
      </button>
    </nav>

    @if (tab() === 'details') {
    <!-- Shortcuts -->
    <section class="mb-4 rounded-xl border border-border bg-card p-4">
      <h3 class="mb-3 flex items-center gap-2 text-base font-semibold text-fg">
        <ng-icon name="lucideExternalLink" size="16" />
        Shortcuts
      </h3>
      <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        @if (merchantBlock(); as m) {
          <a class="flex items-center gap-3 rounded-lg border border-border bg-card p-3 text-fg no-underline transition-colors hover:bg-row-hover" [routerLink]="shellRoutes.commands('manage', 'merchants', str(m['id']))">
            <span class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-row-hover text-brand"><ng-icon name="lucideStore" size="18" /></span>
            <div class="flex min-w-0 flex-1 flex-col">
              <span class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Merchant</span>
              <span class="truncate text-sm font-semibold text-fg">{{ str(m['name']) }}</span>
            </div>
            <ng-icon class="shrink-0 text-muted" name="lucideExternalLink" size="14" />
          </a>
        }
        @if (applicationBlock(); as a) {
          <a class="flex items-center gap-3 rounded-lg border border-border bg-card p-3 text-fg no-underline transition-colors hover:bg-row-hover" [routerLink]="shellRoutes.commands('manage', 'merchant-applications', str(a['id']))">
            <span class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-row-hover text-brand"><ng-icon name="lucideBuilding2" size="18" /></span>
            <div class="flex min-w-0 flex-1 flex-col">
              <span class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Application</span>
              <span class="truncate text-sm font-semibold text-fg">{{ str(a['name']) }}</span>
              <span class="truncate text-xs text-muted">{{ str(a['client_id']) }}</span>
            </div>
            <ng-icon class="shrink-0 text-muted" name="lucideExternalLink" size="14" />
          </a>
        }
        @if (hubWallet(); as hw) {
          <a class="flex items-center gap-3 rounded-lg border border-border bg-card p-3 text-fg no-underline transition-colors hover:bg-row-hover" [routerLink]="walletDetailUrl(str(hw['id']))">
            <span class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-row-hover text-brand"><ng-icon name="lucideWallet" size="18" /></span>
            <div class="flex min-w-0 flex-1 flex-col">
              <span class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Wallet</span>
              <span class="truncate text-sm font-semibold text-fg">{{ str(hw['label']) }}</span>
              <span class="truncate text-xs text-muted">{{ str(hw['subtitle']) }}</span>
            </div>
            <ng-icon class="shrink-0 text-muted" name="lucideExternalLink" size="14" />
          </a>
        }
        <a class="flex items-center gap-3 rounded-lg border border-border bg-card p-3 text-fg no-underline transition-colors hover:bg-row-hover" [routerLink]="shellRoutes.commands('manage', 'webhook-events')" [queryParams]="{ search: id }">
          <span class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-row-hover text-brand"><ng-icon name="lucideLayers" size="18" /></span>
          <div class="flex min-w-0 flex-1 flex-col">
            <span class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Webhook events</span>
            <span class="truncate text-sm font-semibold text-fg">Filter by this record</span>
          </div>
          <ng-icon class="shrink-0 text-muted" name="lucideExternalLink" size="14" />
        </a>
      </div>
    </section>

    @if (applicationBlock()) {
      <section class="mb-4 rounded-xl border border-border bg-card p-4">
        <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 class="mb-0 flex items-center gap-2 text-base font-semibold text-fg">
            <ng-icon name="lucideShieldCheck" size="16" />
            Application API scopes
          </h3>
          @if (!scopesEditing()) {
            <button type="button" uiButton variant="outline" size="sm" (click)="startEditScopes()">Edit scopes</button>
          }
        </div>
        @if (scopesError()) {
          <div class="mb-2 rounded-lg border border-danger-border bg-danger-bg px-3 py-2 text-sm text-danger-fg" role="alert">{{ scopesError() }}</div>
        }
        @if (scopesEditing()) {
          <p class="mb-2 text-xs text-muted">
            Empty list blocks API access. Use &quot;All scopes&quot; for full access.
          </p>
          <div class="mb-2 flex flex-wrap gap-1.5">
            @for (choice of scopeChoicesHub(); track choice.value) {
              <label class="inline-flex cursor-pointer select-none items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors" [class]="isScopeSelectedHub(choice.value) ? 'border-success-border bg-success-bg text-success-fg' : 'border-border bg-card text-fg hover:border-border-strong hover:bg-surface-muted'">
                <input type="checkbox" class="hidden" [checked]="isScopeSelectedHub(choice.value)" (change)="toggleScopeHub(choice.value)" />
                {{ choice.label }}
              </label>
            }
          </div>
          <div class="flex gap-2">
            <button type="button" uiButton variant="primary" size="sm" [disabled]="scopesSaving()" (click)="saveScopesHub()">
              @if (scopesSaving()) {
                Saving…
              } @else {
                Save scopes
              }
            </button>
            <button type="button" uiButton variant="outline" size="sm" [disabled]="scopesSaving()" (click)="cancelEditScopes()">
              Cancel
            </button>
          </div>
        } @else {
          @if (allowedScopesDisplay().length === 0) {
            <p class="mb-0 text-sm text-muted">No API access (no scopes configured).</p>
          } @else {
            <div class="flex flex-wrap gap-1">
              @for (sc of allowedScopesDisplay(); track sc) {
                <ui-badge variant="neutral">{{ scopeLabelHub(sc) }}</ui-badge>
              }
            </div>
          }
        }
      </section>
    }

    <!-- Grouped record blocks -->
    <div class="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section class="rounded-xl border border-border bg-card p-4 lg:col-span-2">
        <h3 class="mb-2 flex items-center gap-2 text-base font-semibold text-fg">
          <ng-icon name="lucideBanknote" size="16" />
          {{ rootKey === 'batch' ? 'Batch' : rootKey === 'mandate' ? 'Mandate' : 'Transaction' }}
        </h3>
        <span class="inline-flex cursor-pointer items-center gap-1.5 text-sm text-fg" (click)="copyToClipboard(id)" title="Copy">
          <span class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Internal id</span>
          <code class="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-sm">{{ id }}</code>
          <ng-icon name="lucideClipboard" size="14" />
        </span>
      </section>
      @for (group of detailGroups(); track group.title) {
        <section class="rounded-xl border border-border bg-card p-4">
          <h3 class="mb-3 text-base font-semibold text-fg">{{ group.title }}</h3>
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            @for (f of group.fields; track f.key) {
              <div class="flex min-w-0 flex-col">
                <span class="mb-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-muted">{{ f.label }}</span>
                <span class="break-words text-sm text-fg">
                  @if (f.key === 'status') {
                    <span class="inline-block rounded-full px-2.5 py-1 text-[0.8125rem] font-semibold" [class]="getTransactionStatusClass(statusText(f.value).toUpperCase())">{{ cell(f.value) }}</span>
                  } @else if (isBooleanValue(f.value)) {
                    <span class="inline-block rounded-md px-2 py-0.5 text-[0.8125rem] font-semibold" [class]="boolPillClass(f.value)">{{ formatScalar(f.value) }}</span>
                  } @else if (shouldCopyField(f.key)) {
                    <span class="inline-flex cursor-pointer items-center gap-1.5 hover:text-primary" (click)="copyField(f.key, f.value)" title="Copy">
                      {{ cell(f.value) }}
                      <ng-icon name="lucideClipboard" size="14" />
                    </span>
                  } @else {
                    {{ cell(f.value) }}
                  }
                </span>
              </div>
            }
          </div>
        </section>
      }
    </div>
    @if (metadataJson()) {
      <section class="mb-4 rounded-xl border border-border bg-card p-4">
        <h3 class="mb-2 text-base font-semibold text-fg">Metadata</h3>
        <pre class="m-0 max-h-[280px] overflow-auto rounded-md border border-border bg-surface-muted p-3 text-[0.8125rem] text-fg">{{ metadataJson() }}</pre>
      </section>
    }

    @if (feeRows().length > 0) {
      <section class="mb-4 rounded-xl border border-border bg-card p-4">
        <h3 class="mb-3 flex items-center gap-2 text-base font-semibold text-fg">
          <ng-icon name="lucideBanknote" size="16" />
          Fee Breakdown
        </h3>
        <div uiTableWrap>
          <table uiTable>
            <thead>
              <tr>
                <th>Fee Type</th>
                <th>Operation</th>
                <th>Provider</th>
                <th>Entity</th>
                <th class="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              @for (row of feeRows(); track row.id) {
                <tr>
                  <td>{{ row.fee_type }}</td>
                  <td>{{ row.operation_type }}</td>
                  <td>{{ row.provider_code || '—' }}</td>
                  <td>{{ row.financial_entity_code || '—' }}</td>
                  <td class="text-right">{{ formatFeeAmount(row.amount, row.currency) }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </section>
    }

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div>
        <section class="h-full mb-4 rounded-xl border border-border bg-card p-4">
          <h3 class="mb-3 flex items-center gap-2 text-base font-semibold text-fg">Journal entries</h3>
          @if (asArray(h['journal_entries']).length) {
            <div uiTableWrap>
              <table uiTable>
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Reference</th>
                    <th>Amounts</th>
                    <th>Reversed</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of asArray(h['journal_entries']); track $index) {
                    @if (asRecord(row); as je) {
                      <tr>
                        <td><span class="text-sm font-semibold">{{ str(je['event_type']) }}</span></td>
                        <td>
                          <code class="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-xs">{{ str(je['transaction_reference']) }}</code>
                        </td>
                        <td class="break-words text-[0.8125rem] leading-snug">{{ journalLineSummary(je['lines']) }}</td>
                        <td>
                          @if (je['is_reversed'] === true) {
                            <span class="inline-block rounded-md bg-danger-bg px-2 py-0.5 text-[0.8125rem] font-semibold text-danger-fg">Yes</span>
                          } @else {
                            <span class="inline-block rounded-md bg-success-bg px-2 py-0.5 text-[0.8125rem] font-semibold text-success-fg">No</span>
                          }
                        </td>
                        <td class="text-sm text-muted">{{ str(je['created_at']) }}</td>
                      </tr>
                      @if (str(je['description'])) {
                        <tr>
                          <td colspan="5" class="!pt-0 !pb-2 text-[0.8125rem] text-muted">{{ str(je['description']) }}</td>
                        </tr>
                      }
                    }
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <p class="mb-0 text-sm text-muted">No journal entries linked to this object.</p>
          }
        </section>
      </div>
      <div>
        <section class="h-full mb-4 rounded-xl border border-border bg-card p-4">
          <h3 class="mb-3 flex items-center gap-2 text-base font-semibold text-fg">Wallet statements</h3>
          @if (asArray(h['wallet_statements']).length) {
            <div uiTableWrap>
              <table uiTable>
                <thead>
                  <tr>
                    <th>Wallet</th>
                    <th>Type</th>
                    <th class="text-right">Amount</th>
                    <th class="text-right">Balance after</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of asArray(h['wallet_statements']); track $index) {
                    @if (asRecord(row); as ws) {
                      <tr>
                        <td>
                          <a class="text-sm font-semibold text-primary hover:underline" [routerLink]="walletDetailUrl(str(ws['wallet_id']))">{{
                            str(ws['wallet_label'])
                          }}</a>
                          <div class="text-xs text-muted">{{ str(ws['wallet_subtitle']) }}</div>
                        </td>
                        <td>{{ str(ws['entry_type']) }}</td>
                        <td class="text-right font-mono">{{ str(ws['amount']) }}</td>
                        <td class="text-right font-mono">{{ str(ws['balance_after']) }}</td>
                        <td class="text-sm text-muted">{{ str(ws['created_at']) }}</td>
                      </tr>
                      @if (str(ws['description'])) {
                        <tr>
                          <td colspan="5" class="!pt-0 !pb-2 text-[0.8125rem] text-muted">{{ str(ws['description']) }}</td>
                        </tr>
                      }
                    }
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <p class="mb-0 text-sm text-muted">
              No wallet statement rows linked to this transaction. Statements appear after ledger posts to a wallet (e.g.
              successful deposit).
            </p>
          }
        </section>
      </div>
    </div>

    @if (entityConfig.key === 'deposits' && canRefundDeposit()) {
      <section class="mb-4 rounded-xl border border-border bg-card p-4">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 class="mb-1 flex items-center gap-2 text-base font-semibold text-fg">Refund</h3>
            <p class="mb-0 text-sm text-muted">
              Refund all or part of this deposit. Confirm with your account password.
              @if (refundedAmount()) {
                Already refunded: <strong>{{ refundedAmount() }}</strong>@if (refundableAmount()) {, still refundable: <strong>{{ refundableAmount() }}</strong>}.
              }
            </p>
          </div>
          <button type="button" uiButton variant="primary" [disabled]="actionBusy()" (click)="openRefundModal()">
            Open refund…
          </button>
        </div>
      </section>
    }
    }
    <!-- Webhooks tab: list with inline event detail -->
    @if (tab() === 'webhooks') {
      <section class="mb-4 rounded-xl border border-border bg-card p-4">
        <h3 class="mb-3 flex items-center gap-2 text-base font-semibold text-fg">
          <ng-icon name="lucideLayers" size="16" />
          Webhook events
        </h3>
        @for (row of asArray(h['webhooks']); track $index) {
          @if (asRecord(row); as w) {
            <div class="mb-2 rounded-lg border border-border">
              <div class="flex flex-wrap items-center gap-3 p-3">
                <button type="button" class="cursor-pointer border-0 bg-transparent p-0 text-left text-sm font-semibold text-fg hover:text-primary" (click)="toggleWebhook(str(w['id']))">
                  {{ openWebhookId() === str(w['id']) ? '▾' : '▸' }} {{ str(w['event_type']) }}
                </button>
                <span class="rounded-2xl px-2.5 py-1 text-xs font-semibold uppercase tracking-wide" [class]="getStatusClass(str(w['status']))">{{ str(w['status']) }}</span>
                <code class="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-xs">{{ str(w['provider_code']) }}</code>
                <span class="text-xs text-muted">attempts: {{ str(w['forward_attempts']) }}@if (w['forward_response_code']) { · HTTP {{ str(w['forward_response_code']) }} }</span>
                <span class="text-xs text-muted">{{ str(w['created_at']) }}</span>
                <span class="ml-auto whitespace-nowrap">
                  <a uiButton variant="outline" size="sm" class="me-1" [routerLink]="webhookDetailUrl(str(w['id']))">Open</a>
                  @if (canResendRow(w)) {
                    <button type="button" uiButton variant="primary" size="sm" class="me-1" [disabled]="rowBusy() === str(w['id'])" (click)="resendWebhookRow(str(w['id']))" title="Resend to merchant">
                      <ng-icon name="lucideSend" size="12" />
                    </button>
                  }
                  @if (canRetryRow(w)) {
                    <button type="button" uiButton variant="secondary" size="sm" [disabled]="rowBusy() === str(w['id'])" (click)="retryWebhookRow(str(w['id']))" title="Retry processing">
                      <ng-icon name="lucideRefreshCw" size="12" />
                    </button>
                  }
                </span>
              </div>
              @if (webhookFailureDetail(w); as detail) {
                <p class="m-0 px-3 pb-2 text-[0.8125rem]" [class]="isWebhookFailure(w) ? 'text-danger-fg' : 'text-fg-secondary'">{{ detail }}</p>
              }
              @if (openWebhookId() === str(w['id'])) {
                <div class="border-t border-border bg-surface-muted p-3">
                  @if (webhookDetailLoading()) {
                    <div class="flex items-center gap-2 text-sm text-muted"><ui-spinner size="sm" /> Loading event…</div>
                  } @else if (webhookDetailError()) {
                    <p class="m-0 text-sm text-danger-fg">{{ webhookDetailError() }}</p>
                  } @else if (webhookDetail(); as ev) {
                    <div class="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      <div><div class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Forwarded to</div><div class="break-all text-sm">{{ ev.forwarded_to_url || '—' }}</div></div>
                      <div><div class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Forwarded at</div><div class="text-sm">{{ ev.forwarded_at || '—' }}</div></div>
                      <div><div class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Response code</div><div class="text-sm">{{ ev.forward_response_code ?? '—' }}</div></div>
                      <div><div class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Provider transaction</div><div class="break-all text-sm">{{ ev.provider_transaction_id || '—' }}</div></div>
                      <div><div class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Application</div><div class="text-sm">{{ ev.application_name || '—' }}</div></div>
                      <div><div class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Matched rules</div><div class="text-sm">{{ ev.matched_rules.length ? ev.matched_rules.length : '—' }}</div></div>
                    </div>
                    <div class="grid grid-cols-1 gap-3 xl:grid-cols-2">
                      <div><div class="mb-1 text-[0.8125rem] font-semibold text-muted">Provider payload</div><pre class="m-0 max-h-[260px] overflow-auto rounded-md border border-border bg-card p-3 text-xs text-fg">{{ json(ev.payload) }}</pre></div>
                      <div><div class="mb-1 text-[0.8125rem] font-semibold text-muted">Forwarded request body</div><pre class="m-0 max-h-[260px] overflow-auto rounded-md border border-border bg-card p-3 text-xs text-fg">{{ json(ev.forward_request_body) }}</pre></div>
                      <div><div class="mb-1 text-[0.8125rem] font-semibold text-muted">Merchant response</div><pre class="m-0 max-h-[260px] overflow-auto rounded-md border border-border bg-card p-3 text-xs text-fg">{{ json(ev.forward_response_body) }}</pre></div>
                      <div><div class="mb-1 text-[0.8125rem] font-semibold text-muted">Provider headers</div><pre class="m-0 max-h-[260px] overflow-auto rounded-md border border-border bg-card p-3 text-xs text-fg">{{ json(ev.headers) }}</pre></div>
                    </div>
                  }
                </div>
              }
            </div>
          }
        }
        @if (!asArray(h['webhooks']).length) {
          <p class="mb-0 mt-2 text-sm text-muted">No webhook events matched this record.</p>
        }
      </section>
    }
  }
</div>

@if (refundModalOpen()) {
  <ui-modal title="Confirm refund" [open]="true" (closed)="closeRefundModal()">
    @if (depositRefundSummary(); as dep) {
      <p class="mb-3 text-sm text-fg-secondary">
        Refund to the customer part or all of the deposit of
        <strong>{{ dep.amount }} {{ dep.currency }}</strong>.
        @if (refundableAmount()) {
          Refundable now: <strong>{{ refundableAmount() }} {{ dep.currency }}</strong>.
        }
      </p>
    }
    <ui-form-field label="Amount to refund" for="refund-amount">
      <input
        id="refund-amount"
        uiInput
        [(ngModel)]="refundAmount"
        name="refundAmount"
        inputmode="decimal"
        autocomplete="off"
      />
    </ui-form-field>
    <ui-form-field label="Reason type" for="refund-code">
      <select id="refund-code" uiInput [(ngModel)]="refundCode" name="refundCode">
        <option value="CUSTOMER_REQUEST">Customer request</option>
        <option value="DUPLICATE">Duplicate payment</option>
        <option value="SERVICE_NOT_DELIVERED">Service not delivered</option>
        <option value="FRAUD">Fraud</option>
        <option value="OTHER">Other</option>
      </select>
    </ui-form-field>
    <ui-form-field label="Reason" for="refund-reason">
      <input
        id="refund-reason"
        uiInput
        [(ngModel)]="refundReason"
        name="refundReason"
        autocomplete="off"
        placeholder="Required"
      />
    </ui-form-field>
    <ui-form-field label="Your password" for="refund-pw">
      <input
        id="refund-pw"
        type="password"
        uiInput
        [(ngModel)]="refundPassword"
        name="refundPassword"
        autocomplete="current-password"
        placeholder="Confirm your user password"
      />
    </ui-form-field>
    @if (refundError()) {
      <div class="rounded-lg border border-danger-border bg-danger-bg px-3 py-2 text-sm text-danger-fg" role="alert">{{ refundError() }}</div>
    }
    <ng-container modal-footer>
      <button type="button" uiButton variant="outline" (click)="closeRefundModal()">Cancel</button>
      <button
        type="button"
        uiButton
        variant="primary"
        [disabled]="actionBusy() || !refundReason.trim() || !refundPassword || !refundAmount.trim()"
        (click)="submitRefund()"
      >
        @if (actionBusy()) {
          <ui-spinner size="sm" />
        }
        Submit refund
      </button>
    </ng-container>
  </ui-modal>
}
